import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { TopologyPayload } from "./types/topology";
import type { CatalogPayload } from "./types/catalog";
import type { AvailabilityPayload, EventsPayload } from "./types/demo";

// "/" by default; set via `vite build --base=/some-prefix/` when the app is
// served under a sub-path behind a reverse proxy. Cast because the project
// doesn't ship the vite/client type shim.
export const BASE_URL: string =
  (import.meta as unknown as { env: { BASE_URL: string } }).env.BASE_URL;

const API_BASE = `${BASE_URL}api/v1`;

export interface Tail {
  tail: string;
  code: string;
  key_site_id: string;
}

export interface Variant {
  variant_id: string;
  config_ref: string;
  cpdata_md5: string;
  name: string;
  slug: string;
  fleet_ref: string;
  tails_count: number;
  tails?: Tail[];
}

async function fetchJson<T>(path: string): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`);
  if (!r.ok) {
    throw new Error(`${r.status} ${r.statusText}: ${path}`);
  }
  return r.json() as Promise<T>;
}

export function useVariants() {
  return useQuery({
    queryKey: ["variants"],
    queryFn: () => fetchJson<{ variants: Variant[] }>("/variants"),
  });
}

export function useTopology(variantId: string | undefined) {
  return useQuery({
    queryKey: ["topology", variantId],
    queryFn: () => fetchJson<TopologyPayload>(`/fleet/${variantId}/topology`),
    enabled: Boolean(variantId),
  });
}

export function useCatalog(variantId: string | undefined) {
  return useQuery({
    queryKey: ["catalog", variantId],
    queryFn: () => fetchJson<CatalogPayload>(`/fleet/${variantId}/catalog`),
    enabled: Boolean(variantId),
  });
}

/** Demo availability trips (dummy data staged for customer demos). */
export function useAvailability(variantId: string | undefined) {
  return useQuery({
    queryKey: ["availability", variantId],
    queryFn: () => fetchJson<AvailabilityPayload>(`/fleet/${variantId}/availability`),
    enabled: Boolean(variantId),
    staleTime: Infinity,
  });
}

/** Fleet-specific event catalog + layer buckets. */
export function useEvents(variantId: string | undefined) {
  return useQuery({
    queryKey: ["events", variantId],
    queryFn: () => fetchJson<EventsPayload>(`/fleet/${variantId}/events`),
    enabled: Boolean(variantId),
    staleTime: Infinity,
  });
}


// ---- UI copy notes (editable, versioned) -----------------------------------
// Latest version per key is what the UI displays; edits append versions to
// the backend store (JSON for the demo, ClickHouse-shaped for later).

export interface UiNote {
  key: string;
  text: string;
  source?: string;
  ui_location?: string;
  version: number;
  updated_at?: string | null;
  author?: string;
}

export interface NotesPayload {
  notes: Record<string, UiNote>;
  editable: boolean;
}

export function useUiNotes() {
  return useQuery({
    queryKey: ["notes"],
    queryFn: () => fetchJson<NotesPayload>("/notes"),
    staleTime: 60_000,
    // Static demo hosts have no notes API — fail fast to the CSV fallback.
    retry: false,
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { key: string; text: string; source?: string }) => {
      const r = await fetch(`${API_BASE}/notes/${encodeURIComponent(p.key)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: p.text, source: p.source }),
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json() as Promise<UiNote>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
  });
}
